import React, { useState, useMemo, useEffect } from "react";
import { Tree, Button } from "antd";
import { useQuery } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";

import { GET_OPPORTUNITY_STATUS_NEW } from "../../../../graphql/query";
import {
  setOpportunityStatusFilters,
  setOpportunityStatusFilterCount,
} from "../../../../redux/slices/leadSlice";
import CustomSkeleton from "../../../commonComponents/CustomSkeleton";

const computeParentCount = (checkedKeys, treeData) => {
  const parentMap = {};
  const buildMap = (nodes, parentKey = null) => {
    nodes.forEach(node => {
      parentMap[String(node.key)] = parentKey;
      if (node.children?.length > 0) buildMap(node.children, String(node.key));
    });
  };
  buildMap(treeData);
  const checkedSet = new Set(checkedKeys.map(String));
  return checkedKeys.filter(key => {
    const parent = parentMap[String(key)];
    return !parent || !checkedSet.has(parent);
  }).length;
};

const getLocationIds = () => {
  const locationId = sessionStorage.getItem("currentLocationId");
  const ids = String(locationId || "").includes(",") ? locationId.split(",") : [locationId];
  return ids.map(id => +id);
};

const transformStatus = status => {
  const transformed = {
    title: status.name?.replace(/_/g, " "),
    key: String(status.key),
  };
  if (status.childs && status.childs.length > 0) {
    transformed.children = status.childs
      .map(child => transformStatus(child))
      .sort((a, b) => a.title?.toLowerCase().localeCompare(b.title?.toLowerCase()));
  }
  return transformed;
};

const OpportunityStatusContent = ({ search = "", scrollListToTop, onClear }) => {
  const dispatch = useDispatch();
  const opportunityStatusFilters =
    useSelector(state => state.lead.filters.opportunityStatusFilters) || [];
  const [expandedNodes, setExpandedNodes] = useState([]);

  const { data, loading } = useQuery(GET_OPPORTUNITY_STATUS_NEW, {
    variables: {
      locationIds: getLocationIds(),
    },
    fetchPolicy: "cache-first",
  });

  const statusTreeData = useMemo(() => {
    const statuses = data?.getOpportunityStatusNew || [];
    return statuses
      .map(status => transformStatus(status))
      .sort((a, b) => a.title?.toLowerCase().localeCompare(b.title?.toLowerCase()));
  }, [data]);

  const filteredTreeData = useMemo(() => {
    if (!search) return statusTreeData;
    const lowerSearch = search.toLowerCase();
    const filterNodes = nodes =>
      nodes
        .map(node => {
          const filteredChildren = node.children ? filterNodes(node.children) : [];
          const selfMatch = node.title?.toLowerCase().includes(lowerSearch);
          if (selfMatch || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren.length > 0 ? filteredChildren : node.children,
            };
          }
          return null;
        })
        .filter(Boolean);
    return filterNodes(statusTreeData);
  }, [statusTreeData, search]);

  // Auto-expand all parent nodes when searching
  useEffect(() => {
    if (search) {
      const keys = [];
      const collect = nodes =>
        (nodes || []).forEach(n => {
          if (n.children?.length > 0) {
            keys.push(n.key);
            collect(n.children);
          }
        });
      collect(filteredTreeData);
      setExpandedNodes(keys);
    }
  }, [filteredTreeData, search]);

  const allParentKeys = useMemo(() => {
    const keys = [];
    const collect = nodes =>
      (nodes || []).forEach(n => {
        if (n.children?.length > 0) {
          keys.push(n.key);
          collect(n.children);
        }
      });
    collect(filteredTreeData);
    return keys;
  }, [filteredTreeData]);

  const isAllExpanded = expandedNodes.length >= allParentKeys.length;

  if (loading) {
    return (
      <div className="px-3 py-2">
        <CustomSkeleton
          skeletonCount={5}
          skeletonMarginBottom="7px"
          skeletonWidth="28rem"
          skeletonHeight="2rem"
        />
      </div>
    );
  }

  return (
    <div className="px-3 py-2">
      <div className="flex items-center justify-between mb-2">
        {allParentKeys.length > 0 ? (
          <Button
            type="link"
            size="small"
            className="!p-0 !h-auto"
            onClick={() => setExpandedNodes(isAllExpanded ? [] : allParentKeys)}
          >
            {isAllExpanded ? "Collapse all" : "Expand all"}
          </Button>
        ) : (
          <span />
        )}
        {onClear && opportunityStatusFilters.length > 0 && (
          <Button type="link" size="small" className="!p-0 !h-auto" onClick={onClear}>
            Clear
          </Button>
        )}
      </div>
      <Tree
        checkable
        checkedKeys={opportunityStatusFilters.map(String)}
        expandedKeys={expandedNodes}
        onExpand={keys => setExpandedNodes([...keys])}
        onCheck={checkedKeys => {
          const keys = Array.isArray(checkedKeys) ? checkedKeys : [];
          dispatch(setOpportunityStatusFilters(keys));
          dispatch(setOpportunityStatusFilterCount(computeParentCount(keys, statusTreeData)));
          scrollListToTop?.();
        }}
        treeData={filteredTreeData}
      />
    </div>
  );
};

export default OpportunityStatusContent;
